import { v4 as uuidv4 } from 'uuid';
import { useEffect, useMemo, useState } from 'react';
import { CountryObject, digitCounts, i18nService } from '@zonofi/i18n';
import {
  APP_TYPE,
  BeatPlanTypeEnum,
  dispatch,
  ErrorVariants,
  getAppType,
  getCurrentCustomerLocation,
  getHasDefaultCfas,
  getHasDefaultDivision,
  getIsZoaiCustomerFlowState,
  getNonPeriodicBeatPlanInfo,
  getPeriodicBeatPlanInfo,
  getUserData,
  getWahaConfigId,
  getWorkspace,
  getWorkspaceId,
  ParseCustomerResponse,
  removeSearchKey,
  showToast,
  updateCurrentCustomerLocation,
  updateExtraDetails,
  updateHasDefaultCfas,
  updateHasDefaultDivisions,
  updateNewCustomerPayload,
  updateZoaiCustomerFlowState,
  useAppSelector,
  useCreateNewCustomerMutation,
  useLazyGetAllStatesQuery,
  useLazyGetCfaListQuery,
  useLazyGetDivisionsQuery,
  useLazyGetSingleMessageQuery,
  useParseCustomerMutation,
  usePublishMessageMutation,
} from '@zonofi/zono-money-store';
import { useNavigate } from 'react-router-dom';
import { camelCase, isNil, startCase } from 'lodash';
import {
  FIELD_OPS_ROUTES,
  MessageType,
  SELLER_WEBAPP_ROUTES,
} from '../../../constants';
import { appendCountryCode } from '@zonofi/common/functions';
import { addCustomerModalGroupRef } from '../modals';
import { validateAddCustomerForm } from '../../helper';
import { useResetFlow } from '../../hook';
import { useQueryParams } from '../../../hooks';
import { useGetBeatPlanType } from './use-get-beat-plan-type';

const DEFAULT_FORM_DATA = {
  country: 'IN',
};

const customerDetails = [
  {
    key: 'firmName',
    label: 'Firm Name',
    placeholder: 'Enter Firm Name',
    required: true,
    parseKey: 'companyName',
  },
  {
    key: 'customerName',
    label: 'Customer Name',
    placeholder: 'Enter Customer Name',
    required: true,
    parseKey: 'name',
  },
  {
    key: 'mobileNumber',
    label: 'Mobile Number',
    placeholder: 'Enter Mobile Number',
    required: true,
    keyboard: 'number',
    parseKey: 'mobile_number_1',
  },
  {
    key: 'customerCode',
    label: 'Customer Code',
    placeholder: 'Enter Customer Code',
    required: true,
    parseKey: 'customer_code',
  },
  {
    key: 'emailId',
    label: 'Email ID',
    placeholder: 'Enter Email ID',
    required: false,
    parseKey: 'email',
  },
];

const addressDetails = [
  {
    key: 'address',
    label: 'Address',
    placeholder: 'Enter Address',
    required: true,
    parseKey: 'address',
  },
  {
    key: 'pincode',
    label: 'Pincode',
    placeholder: 'Enter Pincode',
    required: false,
    keyboard: 'number',
  },
  {
    key: 'city',
    label: 'City',
    placeholder: 'Enter City',
    required: false,
  },
  {
    key: 'state',
    label: 'State',
    placeholder: 'Select State',
    required: false,
    type: 'dropdown',
  },
  {
    key: 'country',
    label: 'Country',
    placeholder: '',
    required: false,
    isDisable: true,
  },
  {
    key: 'lat',
    label: 'Lat',
    placeholder: 'Latitude',
    required: false,
    isDisable: true,
  },
  {
    key: 'lng',
    label: 'Lon',
    placeholder: 'Logitude',
    required: false,
    isDisable: true,
  },
];

export const useAddCustomer = (swPathList?: string[]) => {
  const navigate = useNavigate();

  const appType = useAppSelector(getAppType);
  const workspace = useAppSelector(getWorkspace);
  const workspaceId = useAppSelector(getWorkspaceId);
  const hasDefaultCfas = useAppSelector(getHasDefaultCfas);
  const hasDefaultDivision = useAppSelector(getHasDefaultDivision);
  const nonPeriodicBeatPlanInfo = useAppSelector(getNonPeriodicBeatPlanInfo);
  const periodicBeatPlanInfo = useAppSelector(getPeriodicBeatPlanInfo);
  const currentCustomerLocation = useAppSelector(getCurrentCustomerLocation);
  const { state: isZoaiCustomerFlow } = useAppSelector(
    getIsZoaiCustomerFlowState
  );
  const user = useAppSelector(getUserData);
  const waConfigId = useAppSelector(getWahaConfigId);

  const { handleBack } = useResetFlow();
  const { channelId, messageId } = useQueryParams();

  const [formData, setFormData] =
    useState<Record<string, string>>(DEFAULT_FORM_DATA);
  const [isSameAddress, setIsSameAddress] = useState(true);
  const [statesList, setStatesList] = useState<
    { label: string; value: string }[]
  >([]);
  const [showCountryListModal, setShowCountryListModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryObject>({
    name: 'India',
    flag: '🇮🇳',
    code: 'IN',
    dial_code: '+91',
    currency_code: 'INR',
    time_zones: ['Asia/Kolkata'],
  });
  const [isParseLoading, setIsParseLoading] = useState(false);

  const [sendMessage] = usePublishMessageMutation();
  const [fetchCFAsList] = useLazyGetCfaListQuery();
  const [fetchDivisionsAPI] = useLazyGetDivisionsQuery();
  const [getStates] = useLazyGetAllStatesQuery();
  const [createCustomer, createCustomerResponse] =
    useCreateNewCustomerMutation();
  const [getSingleMessageApi, singleMessageResponse] =
    useLazyGetSingleMessageQuery();
  const [parseCustomerApi, parseCustomerResponse] = useParseCustomerMutation();

  useEffect(() => {
    return () => {
      dispatch(removeSearchKey());
    };
  }, []);

  useGetBeatPlanType();

  useEffect(() => {
    Promise.allSettled([
      fetchCFAsList({
        workspaceId: workspaceId,
        isDefault: true,
      }).unwrap(),
      fetchDivisionsAPI({
        workspaceId: workspaceId,
        isDefault: true,
      }).unwrap(),
      getStates().unwrap(),
    ]).then((results) => {
      const [cfasResult, divisionsResult, statesResult] = results;

      if (cfasResult.status === 'fulfilled') {
        dispatch(
          updateHasDefaultCfas(cfasResult.value?.cfas.some((c) => c.isDefault))
        );
      }

      if (divisionsResult.status === 'fulfilled') {
        dispatch(
          updateHasDefaultDivisions(
            divisionsResult.value?.divisions?.some((d) => d.isDefault)
          )
        );
      }

      if (statesResult.status === 'fulfilled') {
        const data =
          statesResult.value?.map((state: any) => ({
            label: startCase(camelCase(state.name)),
            value: startCase(camelCase(state.name)),
          })) ?? [];
        setStatesList(data);
      }
    });
  }, []);

  useEffect(() => {
    if (channelId && messageId) {
      setIsParseLoading(true);
      // Reset to clean slate before fetching new message
      setFormData(DEFAULT_FORM_DATA);
      dispatch(updateCurrentCustomerLocation(undefined));
      getSingleMessageApi({
        messageId,
        workspaceId: workspaceId,
        channelId,
        sellerWorkspaceId: workspaceId,
      });
      dispatch(
        updateZoaiCustomerFlowState({ state: true, channel: channelId })
      );
    }
  }, [channelId, messageId]);

  useEffect(() => {
    if (singleMessageResponse.isSuccess && singleMessageResponse.data) {
      const { message } = singleMessageResponse.data?.data;
      parseCustomerApiCall(message?.parsedMediaText || message?.text);
    } else if (singleMessageResponse.isError) {
      setIsParseLoading(false);
    }
  }, [singleMessageResponse]);

  const parseCustomerApiCall = (messageText: string) => {
    try {
      const formData = new FormData();
      formData.append('text', messageText);

      parseCustomerApi({
        formData: formData,
        sellerWorkspaceId: workspaceId,
      });
    } catch (error) {
      setIsParseLoading(false);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (parseCustomerResponse.isSuccess && parseCustomerResponse.data) {
      const parsedData = parseCustomerResponse.data;
      const mappedData: Record<string, string> = {};

      const allFields = [...customerDetails, ...addressDetails];
      allFields.forEach((field) => {
        if (!field.parseKey) return;
        const key = field.parseKey as keyof ParseCustomerResponse;
        const value = parsedData[key];

        if (value !== null && value !== undefined) {
          let strValue = String(value);

          // Trim mobile number to allowed digit count for selected country
          if (field.parseKey === 'mobile_number_1') {
            const allowedLength = digitCounts[selectedCountry.code] || 10;
            strValue = strValue.slice(0, allowedLength);
          }

          mappedData[field.key] = strValue;
        } else {
          mappedData[field.key] = '';
        }
      });

      setFormData({
        ...DEFAULT_FORM_DATA,
        ...mappedData,
      });
      setIsParseLoading(false);
    } else if (parseCustomerResponse.isError) {
      dispatch(
        showToast({
          message: 'Failed to extract customer details. Please enter manually.',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
      setIsParseLoading(false);
      setFormData(DEFAULT_FORM_DATA);
    }
  }, [parseCustomerResponse]);

  const requiredFields = useMemo(
    () => [
      ...customerDetails.filter((f) => f.required).map((f) => f.key),
      ...addressDetails.filter((f) => f.required).map((f) => f.key),
    ],
    [customerDetails, addressDetails]
  );

  const isFormValid = useMemo(
    () =>
      validateAddCustomerForm({
        formData,
        requiredFields,
        selectedCountryCode: selectedCountry.code,
      }),
    [formData, requiredFields, selectedCountry.code]
  );

  const onAddCustomerPress = () => {
    const fullAddress = [
      formData.address,
      formData.city,
      formData.pincode,
      formData.state,
    ]
      .filter(Boolean)
      .join(', ');

    dispatch(
      updateNewCustomerPayload({
        mobileNumber: formData.mobileNumber,
        customerName: formData.customerName,
        firmName: formData.firmName,
        customerCode: formData.customerCode,
        emailId: formData?.emailId || '',
        address: fullAddress,
        isSameAddress: isSameAddress,
        location: [formData?.lat, formData?.lng],
      })
    );
    if (!hasDefaultCfas || !hasDefaultDivision) {
      const navigationPath =
        appType === APP_TYPE.FIELDS_OPS
          ? FIELD_OPS_ROUTES.ADD_CFA
          : SELLER_WEBAPP_ROUTES.ADD_CFA;
      navigate(navigationPath);
      return;
    }

    createCustomer({
      senderWorkspaceId: workspaceId,
      phone: appendCountryCode(formData.mobileNumber),
      isSelfRegister: true,
      customerDetails: {
        name: formData.customerName,
        companyName: formData.firmName,
        telephone: appendCountryCode(formData.mobileNumber),
        customerCode: formData.customerCode,
        importSource: 'manual',
        ...(formData.emailId && { email: formData.emailId }),
        ...(fullAddress && {
          physicalAddress: { address1: fullAddress },
        }),
        ...(isSameAddress && {
          shippingAddress: {
            shippingAddress1: fullAddress,
            countryCode: 'IN',
          },
        }),
        countryCode: 'IN',
        ...(formData?.lat && formData?.lng
          ? { location: [Number(formData?.lat), Number(formData?.lng)] }
          : {}),
      },
    });
  };

  useEffect(() => {
    if (createCustomerResponse.isSuccess && createCustomerResponse.data?.data) {
      const customer = createCustomerResponse.data?.data;
      if (customer?.isExisting) {
        dispatch(
          showToast({
            message:
              createCustomerResponse.data?.data?.message ||
              'Customer Already Exists',
            type: ErrorVariants.WARNING,
            canDisplay: true,
            responseStatus: 'Error',
          })
        );
        return;
      }

      if (isZoaiCustomerFlow && channelId) {
        sendMessage({
          sellerWorkspaceId: workspace?.id ?? '',
          channelId: channelId,
          message: {
            _id: uuidv4(),
            text: `✅ New Lead Captured!\n\n🏭 ${customer?.customerDetails?.companyName}\n📍 ${customer?.customerDetails?.physicalAddress?.address1}\n👤 ${customer?.customerDetails?.name} · +${customer?.customerDetails?.telephone}\n👨‍💼 Registered by: ${user.firstName}\n\nLead saved to CRM. Welcome message sent to ${customer?.customerDetails?.name}. 📱`,
            type: MessageType.TEXT,
            sId: user?.id,
            cId: channelId,
            cAt: Date.now(),
            user: {
              firstName: user?.firstName,
              lastName: user?.lastName,
              mobile: user?.mobile,
              email: user?.email,
              id: user?.id,
            },
            chnType: 'WAHA',
            waConfigId,
            source: 'system',
          },
        });
      }
      dispatch(
        updateExtraDetails({
          customerId: customer?.inviteId || '',
          customerName: customer?.customerDetails?.name || '',
          companyName: customer?.customerDetails?.companyName || '',
          mobile: customer?.customerDetails?.telephone || '',
        })
      );
      if (swPathList && swPathList.length)
        navigator.serviceWorker.controller?.postMessage({
          type: 'RECORD_MUTATION',
          listPaths: swPathList,
        });
      setTimeout(() => {
        setFormData(DEFAULT_FORM_DATA);
        dispatch(
          showToast({
            message:
              createCustomerResponse.data?.data?.message ||
              'Customer Added Successfully',
            type: ErrorVariants.SUCCESS,
            canDisplay: true,
            responseStatus: 'Success',
          })
        );
        dispatch(updateZoaiCustomerFlowState({ state: false, channel: '' }));
      }, 100);

      if (appType === APP_TYPE.FIELDS_OPS) {
        const beatType =
          nonPeriodicBeatPlanInfo?.beatType ?? periodicBeatPlanInfo;
        navigate(
          beatType === BeatPlanTypeEnum.Routes
            ? FIELD_OPS_ROUTES.ASSIGN_ROUTES
            : FIELD_OPS_ROUTES.ASSIGN_BEAT_PLAN
        );
        return;
      } else if (appType === APP_TYPE.SELLER_HUB) {
        handleBack();
        return;
      }
    }

    if (createCustomerResponse.isError) {
      dispatch(
        showToast({
          message: 'Failed to add new customer',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
    }
  }, [createCustomerResponse]);

  const handleChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (!isNil(currentCustomerLocation)) {
      setFormData((prev) => ({
        ...prev,
        ...currentCustomerLocation,
      }));
    }
  }, [currentCustomerLocation]);

  const onCountrySelect = (item: CountryObject) => {
    setSelectedCountry(item);
    i18nService.setCurrentCountryCode(item.code);
    setShowCountryListModal(false);
  };

  const onUseLocationPress = () => {
    addCustomerModalGroupRef?.current?.opneLocationMoadal();
  };

  return {
    formData,
    handleChange,
    isSameAddress,
    setIsSameAddress,
    statesList,
    selectedCountry,
    showCountryListModal,
    setShowCountryListModal,
    onCountrySelect,
    onAddCustomerPress,
    isFormValid,
    loading: createCustomerResponse.isLoading,
    addressDetails,
    customerDetails,
    customerName: workspace?.spaceName,
    onUseLocationPress,
    addCustomerModalGroupRef,
    handleBack,
    isParseLoading,
  };
};
